import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Mail, 
  Phone, 
  Building,
  MapPin,
  Calendar,
  Star,
  Code,
  Briefcase,
  Users,
  User,
  ExternalLink
} from 'lucide-react';

const Recruitment = () => {
  const { candidates, addCandidate, updateCandidate, deleteCandidate } = useCRM();
  const [showModal, setShowModal] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'developer',
    experience: '',
    location: '',
    salary: '',
    status: 'sourced',
    skills: '',
    notes: '',
    linkedinUrl: '',
    portfolioUrl: '',
    resumeUrl: '',
    availability: '',
    referredBy: '',
    rating: '3'
  });

  const roles = [
    { value: 'developer', label: 'Software Developer', icon: Code, color: 'text-blue-600' },
    { value: 'frontend', label: 'Frontend Developer', icon: Code, color: 'text-green-600' },
    { value: 'backend', label: 'Backend Developer', icon: Code, color: 'text-purple-600' },
    { value: 'fullstack', label: 'Full Stack Developer', icon: Code, color: 'text-indigo-600' },
    { value: 'designer', label: 'Designer (UI/UX)', icon: Briefcase, color: 'text-pink-600' },
    { value: 'product', label: 'Product Manager', icon: Users, color: 'text-orange-600' },
    { value: 'marketing', label: 'Marketing', icon: Users, color: 'text-red-600' },
    { value: 'sales', label: 'Sales', icon: User, color: 'text-yellow-600' },
    { value: 'cto', label: 'Co-founder/CTO', icon: Star, color: 'text-gray-800' },
    { value: 'other', label: 'Other', icon: Briefcase, color: 'text-gray-600' }
  ];

  const statuses = [
    { value: 'sourced', label: 'Sourced', color: 'bg-gray-100 text-gray-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
    { value: 'responded', label: 'Responded', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'screening', label: 'Screening Call', color: 'bg-purple-100 text-purple-800' },
    { value: 'interviewing', label: 'Interviewing', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'technical_test', label: 'Technical Test', color: 'bg-orange-100 text-orange-800' },
    { value: 'final_round', label: 'Final Round', color: 'bg-green-100 text-green-800' },
    { value: 'offer_extended', label: 'Offer Extended', color: 'bg-green-200 text-green-900' },
    { value: 'hired', label: 'Hired', color: 'bg-green-300 text-green-900' },
    { value: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
    { value: 'declined', label: 'Declined Offer', color: 'bg-red-100 text-red-800' }
  ];

  const experienceLevels = [
    { value: 'entry', label: 'Entry Level (0-2 years)' },
    { value: 'junior', label: 'Junior (2-4 years)' },
    { value: 'mid', label: 'Mid Level (4-7 years)' },
    { value: 'senior', label: 'Senior (7-10 years)' },
    { value: 'lead', label: 'Lead/Principal (10+ years)' },
    { value: 'executive', label: 'Executive/C-Level' }
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'developer',
      experience: '',
      location: '',
      salary: '',
      status: 'sourced',
      skills: '',
      notes: '',
      linkedinUrl: '',
      portfolioUrl: '',
      resumeUrl: '',
      availability: '',
      referredBy: '',
      rating: '3'
    });
    setEditingCandidate(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingCandidate) {
      updateCandidate(editingCandidate.id, formData);
    } else {
      addCandidate(formData);
    }
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (candidate) => {
    setFormData(candidate);
    setEditingCandidate(candidate);
    setShowModal(true);
  };

  const filteredCandidates = candidates.filter(candidate => {
    const matchesSearch = candidate.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         candidate.skills.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || candidate.role === filterRole;
    const matchesStatus = filterStatus === 'all' || candidate.status === filterStatus;
    return matchesSearch && matchesRole && matchesStatus;
  });

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];
  const getRoleInfo = (role) => roles.find(r => r.value === role) || roles[0];

  const renderStars = (rating) => {
    const stars = [];
    const numRating = parseInt(rating) || 3;
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Star
          key={i}
          size={16}
          className={i <= numRating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
        />
      );
    }
    return stars;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">First Employee Recruitment</h1>
          <p className="text-gray-600 mt-2">Find and hire your first team members to scale your startup</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Candidate</span>
        </button>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-blue-50">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Candidates</p>
              <p className="text-2xl font-semibold text-gray-900">{filteredCandidates.length}</p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-purple-50">
              <Calendar className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Interviewing</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredCandidates.filter(c => ['interviewing', 'technical_test', 'final_round'].includes(c.status)).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-50">
              <Star className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Offers Extended</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredCandidates.filter(c => c.status === 'offer_extended').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="flex items-center">
            <div className="p-2 rounded-lg bg-green-50">
              <Briefcase className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Hired</p>
              <p className="text-2xl font-semibold text-gray-900">
                {filteredCandidates.filter(c => c.status === 'hired').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search candidates or skills..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            className="input-field"
          >
            <option value="all">All Roles</option>
            {roles.map(role => (
              <option key={role.value} value={role.value}>{role.label}</option>
            ))}
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="input-field"
          >
            <option value="all">All Status</option>
            {statuses.map(status => (
              <option key={status.value} value={status.value}>{status.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Candidates Grid */}
      {filteredCandidates.length === 0 ? (
        <div className="card text-center py-12">
          <Users className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No candidates found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {candidates.length === 0 ? "Start building your team by adding your first candidate." : "Try adjusting your search or filters."}
          </p>
          <div className="mt-6">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Candidate
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredCandidates.map((candidate) => {
            const statusInfo = getStatusInfo(candidate.status);
            const roleInfo = getRoleInfo(candidate.role);
            const experienceInfo = experienceLevels.find(e => e.value === candidate.experience);
            
            return (
              <div key={candidate.id} className="card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{candidate.name}</h3>
                    <div className="flex items-center mt-1">
                      <roleInfo.icon className={`mr-2 h-4 w-4 ${roleInfo.color}`} />
                      <span className="text-gray-600">{roleInfo.label}</span>
                    </div>
                    {candidate.location && (
                      <div className="flex items-center mt-1">
                        <MapPin className="mr-2 h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-500">{candidate.location}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(candidate)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => deleteCandidate(candidate.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <div className="flex items-center space-x-1">
                      {renderStars(candidate.rating)}
                    </div>
                  </div>

                  <div className="space-y-2 text-sm">
                    {experienceInfo && (
                      <div className="flex items-center text-gray-600">
                        <Briefcase className="mr-2 h-4 w-4" />
                        {experienceInfo.label.split(' ')[0]} Level
                      </div>
                    )}
                    {candidate.salary && (
                      <div className="flex items-center text-gray-600">
                        <span className="mr-2">💰</span>
                        {candidate.salary}
                      </div>
                    )}
                    {candidate.availability && (
                      <div className="flex items-center text-gray-600">
                        <Calendar className="mr-2 h-4 w-4" />
                        Available: {candidate.availability}
                      </div>
                    )}
                  </div>

                  {candidate.skills && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Key Skills:</p>
                      <div className="flex flex-wrap gap-1">
                        {candidate.skills.split(',').slice(0, 3).map((skill, index) => (
                          <span key={index} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded">
                            {skill.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {candidate.referredBy && (
                    <div className="text-sm text-blue-600 bg-blue-50 rounded-lg p-2">
                      👥 Referred by: {candidate.referredBy}
                    </div>
                  )}

                  <div className="flex space-x-2 pt-3 border-t border-gray-100">
                    {candidate.email && (
                      <a
                        href={`mailto:${candidate.email}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    {candidate.phone && (
                      <a
                        href={`tel:${candidate.phone}`}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                      >
                        <Phone size={16} />
                      </a>
                    )}
                    {candidate.linkedinUrl && (
                      <a
                        href={candidate.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Building size={16} />
                      </a>
                    )}
                    {candidate.portfolioUrl && (
                      <a
                        href={candidate.portfolioUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-6">
                {editingCandidate ? 'Edit Candidate' : 'Add New Candidate'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    className="input-field"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="tel"
                    placeholder="Phone"
                    className="input-field"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Location"
                    className="input-field"
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.role}
                    onChange={(e) => setFormData({...formData, role: e.target.value})}
                  >
                    {roles.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  <select
                    className="input-field"
                    value={formData.experience}
                    onChange={(e) => setFormData({...formData, experience: e.target.value})}
                  >
                    <option value="">Experience Level</option>
                    {experienceLevels.map(level => (
                      <option key={level.value} value={level.value}>{level.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  <select
                    className="input-field"
                    value={formData.rating}
                    onChange={(e) => setFormData({...formData, rating: e.target.value})}
                  >
                    <option value="1">⭐ 1 Star</option>
                    <option value="2">⭐⭐ 2 Stars</option>
                    <option value="3">⭐⭐⭐ 3 Stars</option>
                    <option value="4">⭐⭐⭐⭐ 4 Stars</option>
                    <option value="5">⭐⭐⭐⭐⭐ 5 Stars</option>
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Salary Expectation (e.g., $80K-$120K)"
                    className="input-field"
                    value={formData.salary}
                    onChange={(e) => setFormData({...formData, salary: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Availability (e.g., 2 weeks notice)"
                    className="input-field"
                    value={formData.availability}
                    onChange={(e) => setFormData({...formData, availability: e.target.value})}
                  />
                </div>

                <input
                  type="text"
                  placeholder="Key Skills (comma separated)"
                  className="input-field"
                  value={formData.skills}
                  onChange={(e) => setFormData({...formData, skills: e.target.value})}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="url"
                    placeholder="LinkedIn URL"
                    className="input-field"
                    value={formData.linkedinUrl}
                    onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})}
                  />
                  <input
                    type="url"
                    placeholder="Portfolio/GitHub URL"
                    className="input-field"
                    value={formData.portfolioUrl}
                    onChange={(e) => setFormData({...formData, portfolioUrl: e.target.value})}
                  />
                </div>

                <input
                  type="text"
                  placeholder="Referred by"
                  className="input-field"
                  value={formData.referredBy}
                  onChange={(e) => setFormData({...formData, referredBy: e.target.value})}
                />

                <textarea
                  placeholder="Notes (interview feedback, strengths, concerns...)"
                  className="input-field h-24 resize-none"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingCandidate ? 'Update' : 'Add'} Candidate
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Recruitment;